import {
  BookingCancellationRequestDto,
  BookingTimeslotCancelInformationDto,
  useBookingCancelInformationQuery,
  useBookingCancellationMutation,
} from "../../api/bookingApi.ts";
import { useParams, useNavigate } from "react-router";
import { useState } from "react";
import ActionButton from "../ActionButton.tsx";
import { Trans } from "@lingui/react/macro";
import LoadingState from "../LoadingState.tsx";
import ErrorState from "../ErrorState.tsx";
import { useLingui } from "@lingui/react";
import { format } from "date-fns";
import { useLanguageStore } from "../../stores/languageStore.ts";

export default function CancelBooking({
  onSuccessPath,
}: {
  onSuccessPath?: string;
}) {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { i18n } = useLingui();
  const { dateFnsLocale } = useLanguageStore();
  const {
    data: cancelInformationDto,
    isLoading,
    isError,
  } = useBookingCancelInformationQuery(bookingId);

  const { mutate: cancelBooking, isPending: isCancelling } =
    useBookingCancellationMutation();

  const [selectedTimeslots, setSelectedTimeslots] = useState<string[]>([]);

  if (isLoading) {
    return (
      <LoadingState message={i18n._("Loading cancellation information...")} />
    );
  }

  if (isError || !cancelInformationDto || !bookingId) {
    return <ErrorState />;
  }

  // --- Select All Logic ---
  const cancellableTimeslots = cancelInformationDto.timeslots.filter(
    (ts) => ts.cancellable,
  );
  const isAllSelected =
    cancellableTimeslots.length > 0 &&
    cancellableTimeslots.every((ts) => selectedTimeslots.includes(ts.id));

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedTimeslots([]);
    } else {
      setSelectedTimeslots(cancellableTimeslots.map((ts) => ts.id));
    }
  };
  // ------------------------

  const handleCheckboxChange = (id: string) => {
    setSelectedTimeslots((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const selectedTotalAmount = cancelInformationDto.timeslots
    .filter((ts) => selectedTimeslots.includes(ts.id))
    .reduce((sum, ts) => sum + ts.cancellableAmount, 0);

  const handleSubmit = () => {
    const request: BookingCancellationRequestDto = {
      bookingCancelInformation: cancelInformationDto,
      timeSlots: selectedTimeslots,
      bookingId: bookingId,
    };
    cancelBooking(request, {
      onSuccess: () => {
        navigate(onSuccessPath ?? "..");
      },
    });
  };

  return (
    <div>
      {cancellableTimeslots.length > 0 && (
        <div className="flex items-center gap-x-4 px-4 pt-4 pb-2">
          <input
            type="checkbox"
            id="select-all"
            className="text-soluud-primary focus:ring-soluud-primary h-5 w-5 cursor-pointer rounded border-gray-300"
            checked={isAllSelected}
            onChange={handleSelectAll}
          />
          <label
            htmlFor="select-all"
            className="text-soluud-text-primary cursor-pointer"
          >
            <Trans>Select All Cancellable Timeslots</Trans>
          </label>
        </div>
      )}
      <div className="flex flex-col gap-6 pt-2">
        <div className="flex flex-col gap-4">
          {cancelInformationDto.timeslots.map(
            (ts: BookingTimeslotCancelInformationDto) => (
              <div
                key={ts.id}
                className={`flex items-center justify-between rounded-lg border p-4 ${
                  ts.cancellable
                    ? "border-gray-200 bg-white"
                    : "border-gray-200 bg-gray-50 opacity-75"
                }`}
              >
                <div className="flex items-center gap-4">
                  <input
                    type="checkbox"
                    className="text-soluud-primary focus:ring-soluud-primary h-5 w-5 rounded border-gray-300 disabled:cursor-not-allowed"
                    checked={selectedTimeslots.includes(ts.id)}
                    onChange={() => handleCheckboxChange(ts.id)}
                    disabled={!ts.cancellable}
                  />
                  <div className="flex flex-col">
                    <span className="text-soluud-text-primary">
                      {format(new Date(ts.timeslot.from), "PPP p", {
                        locale: dateFnsLocale,
                      })}{" "}
                      -{" "}
                      {format(new Date(ts.timeslot.to), "p", {
                        locale: dateFnsLocale,
                      })}
                    </span>
                    {ts.status == "CANCELLED" && (
                      <span className="text-soluud-text-primary text-sm">
                        <Trans>Cancelled</Trans>
                      </span>
                    )}
                    {ts.status != "CANCELLED" && !ts.cancellable && (
                      <span className="text-soluud-warning text-sm">
                        <Trans>Not cancellable</Trans>
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end">
                  <span className="text-soluud-text-primary">
                    <Trans>Total:</Trans> {ts.totalAmount.toFixed(2)} €
                  </span>
                  {ts.cancellable && (
                    <span className="text-green-600">
                      <Trans>To refund:</Trans>{" "}
                      {ts.cancellableAmount.toFixed(2)} €
                    </span>
                  )}
                  {ts.cancelledAmount && (
                    <span className="text-soluud-text-primary">
                      <Trans>Already refunded:</Trans>{" "}
                      {ts.cancelledAmount.toFixed(2)} €
                    </span>
                  )}
                </div>
              </div>
            ),
          )}
        </div>

        <div className="mt-2 border-t pt-4">
          <div className="mb-4 flex items-center justify-between">
            <span className="text-soluud-text-primary text-lg">
              <Trans>Total Refund Amount:</Trans>
            </span>
            <span className="text-xl">{selectedTotalAmount.toFixed(2)} €</span>
          </div>
          <div className="flex justify-end gap-2">
            <ActionButton
              variant="primary"
              disabled={selectedTimeslots.length === 0 || isCancelling}
              isLoading={isCancelling}
              onClick={handleSubmit}
            >
              <Trans>Cancel Timeslots</Trans>
            </ActionButton>
          </div>
        </div>
      </div>
    </div>
  );
}
